import { useEffect, useRef } from 'react';

// Keyboard rule of the workbench (spec, "Responsive behavior and
// accessibility"): the drawer and the switchers trap focus while open and
// close on Escape. One hook, so every panel behaves the same.

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** The keyboard-focusable elements inside a container, in DOM order. */
export function focusableWithin(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => {
    if (el.hasAttribute('hidden')) return false;
    if (el.tagName === 'INPUT' && el.getAttribute('type') === 'hidden') return false;
    if (el.tabIndex < 0) return false;
    return true;
  });
}

/**
 * While active: focus the first control (or initialFocusRef), keep Tab and
 * Shift+Tab inside containerRef, and call onClose on Escape (unless an inner
 * widget already handled it, or it ends an IME composition) and, by default,
 * on a mousedown outside the panel and its trigger. A panel that closes from
 * its own backdrop click passes closeOnOutsidePointer: false, so the panel is
 * not removed on mousedown (which lets a touch browser deliver the click to
 * whatever is underneath). When it stops being active, focus goes back to
 * returnFocusRef.
 */
export function useFocusTrap(
  containerRef,
  { active, onClose, returnFocusRef = null, initialFocusRef = null, closeOnOutsidePointer = true },
) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!active) return undefined;
    const container = containerRef.current;
    const returnTo = returnFocusRef?.current ?? null;
    const first = initialFocusRef?.current ?? focusableWithin(container)[0] ?? container;
    first?.focus?.();

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        // Escape that cancels an IME composition, or that an inner widget
        // (a select, a combobox) already handled, must not close the panel.
        if (event.isComposing || event.defaultPrevented) return;
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab' || !container) return;
      const items = focusableWithin(container);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      const inside = container.contains(document.activeElement);
      if (event.shiftKey && (!inside || document.activeElement === firstItem)) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && (!inside || document.activeElement === lastItem)) {
        event.preventDefault();
        firstItem.focus();
      }
    }

    function onMouseDown(event) {
      if (container?.contains(event.target) || returnTo?.contains?.(event.target)) return;
      onCloseRef.current?.();
    }

    document.addEventListener('keydown', onKeyDown);
    if (closeOnOutsidePointer) document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (closeOnOutsidePointer) document.removeEventListener('mousedown', onMouseDown);
      if (returnTo && document.contains(returnTo)) returnTo.focus();
    };
  }, [active, containerRef, returnFocusRef, initialFocusRef, closeOnOutsidePointer]);
}
